import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { usePico } from '../pico';

/**
 * Side-effect hook that fires `page_displayed` on initial mount and on every
 * non-shallow client-side route change. Must be called inside `PicoProvider`.
 *
 * Shallow `routeChangeComplete` events are skipped — `ReactRouterLayout`
 * bridges its inner react-router navigations to next/router with
 * `shallow: true`, which would otherwise cause duplicate fires per visit.
 *
 * Payload:
 *   - `url` (STRING) — pathname + query + hash (no protocol or host)
 */
export const usePageDisplayedTracking = () => {
  const pico = usePico();
  const router = useRouter();
  const initialFiredRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!initialFiredRef.current) {
      initialFiredRef.current = true;
      const url = window.location.pathname + window.location.search + window.location.hash;
      pico.track('page_displayed', { url });
    }

    const handler = (url: string, options?: { shallow?: boolean }) => {
      if (options?.shallow) return;
      pico.track('page_displayed', { url });
    };

    router.events.on('routeChangeComplete', handler);
    return () => {
      router.events.off('routeChangeComplete', handler);
    };
  }, [pico, router]);
};
